import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarPlus, CloudUpload, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, jsonBody } from './api'
import type { Page } from './types'
import { EmptyState, PageHeader, StatusPill, TableShell } from './ui'

interface Playlist {
  id: string
  name: string
}

interface Device {
  id: string
  name: string
}

interface Group {
  id: string
  name: string
}

interface Schedule {
  id: string
  name: string
  playlistId: string
  timezone: string
  weekdays: number[]
  startTime: string
  endTime: string
  priority: number
  targets: { type: 'DEVICE' | 'GROUP'; id: string }[]
  createdAt: string
}

interface Publication {
  id: string
  version: number
  name: string
  status: string
  manifestCount: number
  fallbackPlaylistId: string
  targeted: boolean
  createdAt: string
}

const weekdays = [
  ['Mon', 1],
  ['Tue', 2],
  ['Wed', 3],
  ['Thu', 4],
  ['Fri', 5],
  ['Sat', 6],
  ['Sun', 7],
] as const

export function SchedulesPage() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [targetType, setTargetType] = useState<'GROUP' | 'DEVICE'>('GROUP')
  const schedules = useQuery({
    queryKey: ['schedules'],
    queryFn: () => api<Schedule[]>('/schedules'),
  })
  const playlists = useQuery({
    queryKey: ['playlists'],
    queryFn: () => api<Playlist[]>('/playlists'),
  })
  const devices = useQuery({
    queryKey: ['devices'],
    queryFn: () => api<Page<Device>>('/devices?limit=100'),
  })
  const groups = useQuery({
    queryKey: ['device-groups'],
    queryFn: () => api<Group[]>('/device-groups'),
  })
  const create = useMutation({
    mutationFn: (input: unknown) =>
      api('/schedules', { method: 'POST', body: jsonBody(input) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['schedules'] })
      setOpen(false)
      toast.success('Schedule created.')
    },
    onError: (error) => toast.error(error.message),
  })
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    create.mutate({
      name: data.get('name'),
      playlistId: data.get('playlistId'),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      startsOn: data.get('startsOn') || null,
      endsOn: data.get('endsOn') || null,
      weekdays: weekdays
        .filter(([_, value]) => data.get(`weekday-${value}`) === 'on')
        .map(([_, value]) => value),
      startTime: data.get('startTime'),
      endTime: data.get('endTime'),
      priority: Number(data.get('priority')),
      targets: [{ type: targetType, id: data.get('targetId') }],
    })
  }
  const targetOptions =
    targetType === 'GROUP' ? groups.data : devices.data?.items
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Content'
        title='Schedules'
        description='Target playlists by date range, weekday, local time, timezone, device or group. Equal-priority conflicts are rejected before they can reach a screen.'
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <CalendarPlus className='size-4' />
                New schedule
              </Button>
            </DialogTrigger>
            <DialogContent className='sm:max-w-2xl'>
              <DialogHeader>
                <DialogTitle>Create a schedule</DialogTitle>
                <DialogDescription>
                  Times are interpreted in the workspace timezone shown below.
                </DialogDescription>
              </DialogHeader>
              <form className='space-y-4' onSubmit={submit}>
                <div className='grid gap-4 sm:grid-cols-2'>
                  <div className='space-y-2'>
                    <Label htmlFor='schedule-name'>Name</Label>
                    <Input id='schedule-name' name='name' required />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='schedule-playlist'>Playlist</Label>
                    <select
                      id='schedule-playlist'
                      name='playlistId'
                      required
                      className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                    >
                      <option value=''>Select playlist</option>
                      {playlists.data?.map((playlist) => (
                        <option key={playlist.id} value={playlist.id}>
                          {playlist.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className='grid gap-4 sm:grid-cols-2'>
                  <div className='space-y-2'>
                    <Label htmlFor='starts-on'>
                      Starts on{' '}
                      <span className='font-normal text-muted-foreground'>
                        (optional)
                      </span>
                    </Label>
                    <Input id='starts-on' name='startsOn' type='date' />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='ends-on'>
                      Ends on{' '}
                      <span className='font-normal text-muted-foreground'>
                        (optional)
                      </span>
                    </Label>
                    <Input id='ends-on' name='endsOn' type='date' />
                  </div>
                </div>
                <fieldset>
                  <legend className='mb-2 text-sm font-medium'>Weekdays</legend>
                  <div className='flex flex-wrap gap-2'>
                    {weekdays.map(([label, value]) => (
                      <label
                        key={value}
                        className='flex items-center gap-2 rounded-md border px-3 py-2 text-sm'
                      >
                        <input
                          type='checkbox'
                          name={`weekday-${value}`}
                          defaultChecked={value <= 5}
                          className='accent-primary'
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <div className='grid gap-4 sm:grid-cols-3'>
                  <div className='space-y-2'>
                    <Label htmlFor='start-time'>Start</Label>
                    <Input
                      id='start-time'
                      name='startTime'
                      type='time'
                      defaultValue='08:00'
                      required
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='end-time'>End</Label>
                    <Input
                      id='end-time'
                      name='endTime'
                      type='time'
                      defaultValue='18:00'
                      required
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='priority'>Priority</Label>
                    <Input
                      id='priority'
                      name='priority'
                      type='number'
                      defaultValue='0'
                      required
                    />
                  </div>
                </div>
                <p className='rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground'>
                  Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                </p>
                <div className='grid gap-4 sm:grid-cols-2'>
                  <div className='space-y-2'>
                    <Label htmlFor='target-type'>Target type</Label>
                    <select
                      id='target-type'
                      className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                      value={targetType}
                      onChange={(event) =>
                        setTargetType(event.target.value as 'GROUP' | 'DEVICE')
                      }
                    >
                      <option value='GROUP'>Device group</option>
                      <option value='DEVICE'>Individual device</option>
                    </select>
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='target-id'>Target</Label>
                    <select
                      id='target-id'
                      name='targetId'
                      required
                      className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                    >
                      <option value=''>Select target</option>
                      {targetOptions?.map((target) => (
                        <option key={target.id} value={target.id}>
                          {target.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <Button className='w-full' disabled={create.isPending}>
                  {create.isPending ? 'Checking conflicts…' : 'Create schedule'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      {schedules.data?.length ? (
        <TableShell
          headers={['Schedule', 'Window', 'Days', 'Priority', 'Target']}
        >
          {schedules.data.map((schedule) => (
            <tr key={schedule.id}>
              <td className='px-4 py-3'>
                <p className='font-semibold'>{schedule.name}</p>
                <p className='mt-1 text-xs text-muted-foreground'>
                  {schedule.timezone}
                </p>
              </td>
              <td className='px-4 py-3 font-mono text-xs'>
                {schedule.startTime}–{schedule.endTime}
              </td>
              <td className='px-4 py-3 text-muted-foreground'>
                {schedule.weekdays
                  .map(
                    (day) => weekdays.find(([_, value]) => value === day)?.[0]
                  )
                  .join(', ')}
              </td>
              <td className='px-4 py-3'>
                <StatusPill value={`P${schedule.priority}`} tone='neutral' />
              </td>
              <td className='px-4 py-3 text-muted-foreground'>
                {schedule.targets[0]?.type ?? '—'}
              </td>
            </tr>
          ))}
        </TableShell>
      ) : (
        <EmptyState
          title='No schedules yet'
          description='Create at least one reusable playlist, then target it to a screen or group.'
          action={
            <Button
              onClick={() => setOpen(true)}
              disabled={!playlists.data?.length}
            >
              Create schedule
            </Button>
          }
        />
      )}
    </div>
  )
}

export function PublishPage() {
  const queryClient = useQueryClient()
  const [fallbackPlaylistId, setFallbackPlaylistId] = useState('')
  const publications = useQuery({
    queryKey: ['publications'],
    queryFn: () => api<Publication[]>('/publications'),
  })
  const playlists = useQuery({
    queryKey: ['playlists'],
    queryFn: () => api<Playlist[]>('/playlists'),
  })
  const selectedFallbackId =
    fallbackPlaylistId ||
    publications.data?.find((publication) => !publication.targeted)
      ?.fallbackPlaylistId ||
    playlists.data?.[0]?.id ||
    ''
  const nextVersion = (publications.data?.[0]?.version ?? 0) + 1
  const publish = useMutation({
    mutationFn: (input: { fallbackPlaylistId: string }) =>
      api<Publication>('/publications/publish', {
        method: 'POST',
        body: jsonBody(input),
      }),
    onSuccess: async (publication) => {
      await queryClient.invalidateQueries({ queryKey: ['publications'] })
      toast.success(`Version ${publication.version} published and signed.`)
    },
    onError: (error) => toast.error(error.message),
  })
  const publishSelected = () => {
    if (!selectedFallbackId) {
      toast.error('Select a default playlist first.')
      return
    }
    publish.mutate({ fallbackPlaylistId: selectedFallbackId })
  }
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Release'
        title='Publish to screens'
        description='Choose the default playlist and publish. The release name and next immutable version number are generated automatically.'
        action={
          <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
            <select
              aria-label='Default fallback playlist'
              className='h-9 min-w-52 rounded-md border bg-background px-3 text-sm'
              value={selectedFallbackId}
              onChange={(event) => setFallbackPlaylistId(event.target.value)}
            >
              <option value=''>Select default playlist</option>
              {playlists.data?.map((playlist) => (
                <option key={playlist.id} value={playlist.id}>
                  {playlist.name}
                </option>
              ))}
            </select>
            <Button
              disabled={!selectedFallbackId || publish.isPending}
              onClick={publishSelected}
            >
              <CloudUpload className='size-4' />
              {publish.isPending
                ? 'Publishing...'
                : `Publish version ${nextVersion}`}
            </Button>
          </div>
        }
      />
      <div className='rounded-xl border bg-muted/30 px-4 py-3 text-sm text-muted-foreground'>
        The most recently published default playlist stays selected for the next
        release. It plays whenever no schedule matches.
      </div>
      {publications.data?.length ? (
        <TableShell
          headers={[
            'Version',
            'Release',
            'Default playlist',
            'Scope',
            'State',
            'Manifests',
            'Created',
          ]}
        >
          {publications.data.map((publication) => (
            <tr key={publication.id}>
              <td className='px-4 py-3 font-mono font-bold'>
                v{publication.version}
              </td>
              <td className='px-4 py-3 font-semibold'>{publication.name}</td>
              <td className='px-4 py-3 text-muted-foreground'>
                {playlists.data?.find(
                  (playlist) => playlist.id === publication.fallbackPlaylistId
                )?.name ?? 'Deleted playlist'}
              </td>
              <td className='px-4 py-3 text-muted-foreground'>
                {publication.targeted ? 'Selected screens' : 'All screens'}
              </td>
              <td className='px-4 py-3'>
                <StatusPill value={publication.status} />
              </td>
              <td className='px-4 py-3'>{publication.manifestCount}</td>
              <td className='px-4 py-3 text-muted-foreground'>
                {new Date(publication.createdAt).toLocaleString()}
              </td>
            </tr>
          ))}
        </TableShell>
      ) : (
        <EmptyState
          title='Nothing published'
          description='Choose a default playlist above. Version 1 will be named automatically and compiled for every active screen.'
          action={
            <Button
              onClick={publishSelected}
              disabled={!selectedFallbackId || publish.isPending}
            >
              <Plus className='size-4' />
              Publish version 1
            </Button>
          }
        />
      )}
    </div>
  )
}
